/**
 * Centralized Error Handling Utilities
 *
 * Provides consistent error handling, logging, and reporting across the application.
 * Integrates with Sentry for error monitoring in production.
 *
 * @module errorHandler
 * @version 1.0.0
 * @date 2025-11-19
 * @related CQ-004 Error Handling Standardization
 */

import { AppError, ErrorCode, ErrorCategory, HttpStatus, isAppError, getErrorMessage } from './AppError';
import { logger } from '../logger';
import { toast } from '@/hooks/use-toast';

// ============================================================================
// ERROR LOGGING
// ============================================================================

/**
 * Log error with appropriate level based on severity
 */
function logError(error: Error | AppError, context?: Record<string, any>): void {
  const errorData = {
    message: error.message,
    stack: error.stack,
    ...context,
    ...(isAppError(error) && {
      code: error.code,
      category: error.category,
      statusCode: error.statusCode,
      isOperational: error.isOperational,
      metadata: error.metadata,
    }),
  };

  // Log at appropriate level
  if (isAppError(error)) {
    if (!error.isOperational) {
      // Programming errors are always logged as errors
      logger.error('Programming error occurred', errorData);
    } else if (error.category === ErrorCategory.VALIDATION) {
      // Validation errors are logged as warnings
      logger.warn('Validation error', errorData);
    } else {
      // Other operational errors logged as info/error depending on severity
      if (error.statusCode >= 500) {
        logger.error('Operational error', errorData);
      } else {
        logger.info('Operational error', errorData);
      }
    }
  } else {
    // Unknown errors are always logged as errors
    logger.error('Unhandled error', errorData);
  }
}

// ============================================================================
// SENTRY INTEGRATION
// ============================================================================

/**
 * Report error to Sentry (if configured)
 */
function reportToSentry(error: Error | AppError, context?: Record<string, any>): void {
  // Check if Sentry is available
  if (typeof window !== 'undefined' && (window as any).Sentry) {
    const Sentry = (window as any).Sentry;

    // Set context
    if (context) {
      Sentry.setContext('error_context', context);
    }

    // Set tags for filtering
    if (isAppError(error)) {
      Sentry.setTag('error_code', error.code);
      Sentry.setTag('error_category', error.category);
      Sentry.setTag('is_operational', error.isOperational);

      // Set user context if available
      if (error.metadata.userId) {
        Sentry.setUser({ id: error.metadata.userId });
      }

      // Only report non-operational errors or high-severity operational errors
      if (!error.isOperational || error.statusCode >= 500) {
        Sentry.captureException(error, {
          level: error.isOperational ? 'warning' : 'error',
          extra: {
            metadata: error.metadata,
            recovery: error.recovery,
          },
        });
      }
    } else {
      // Report unknown errors
      Sentry.captureException(error, {
        level: 'error',
        extra: context,
      });
    }
  }
}

// ============================================================================
// USER NOTIFICATION
// ============================================================================

/**
 * Show error toast notification to user
 */
function notifyUser(error: AppError, options?: { duration?: number; action?: () => void }): void {
  // Don't show toast for certain error types
  const silentErrors = [
    ErrorCategory.AUTHENTICATION, // Handled by auth flow
    ErrorCategory.AUTHORIZATION, // Handled by permission UI
  ];

  if (silentErrors.includes(error.category)) {
    return;
  }

  // Show toast with appropriate variant
  const variant = error.statusCode >= 500 ? 'destructive' : 'default';

  toast({
    title: getErrorTitle(error.category),
    description: error.userMessage,
    variant,
    duration: options?.duration ?? 5000,
    action: options?.action ? {
      label: 'Retry',
      onClick: options.action,
    } : undefined,
  });
}

/**
 * Get user-friendly error title based on category
 */
function getErrorTitle(category: ErrorCategory): string {
  const titles: Record<ErrorCategory, string> = {
    [ErrorCategory.VALIDATION]: 'Validation Error',
    [ErrorCategory.AUTHENTICATION]: 'Authentication Required',
    [ErrorCategory.AUTHORIZATION]: 'Permission Denied',
    [ErrorCategory.NOT_FOUND]: 'Not Found',
    [ErrorCategory.CONFLICT]: 'Conflict',
    [ErrorCategory.RATE_LIMIT]: 'Rate Limit Exceeded',
    [ErrorCategory.DATABASE]: 'Database Error',
    [ErrorCategory.EXTERNAL_API]: 'Service Error',
    [ErrorCategory.FILE_SYSTEM]: 'File Error',
    [ErrorCategory.NETWORK]: 'Network Error',
    [ErrorCategory.PROGRAMMING]: 'System Error',
    [ErrorCategory.UNKNOWN]: 'Error',
  };

  return titles[category] ?? 'Error';
}

// ============================================================================
// MAIN ERROR HANDLER
// ============================================================================

export interface HandleErrorOptions {
  /** Additional context to log */
  context?: Record<string, any>;

  /** Should show toast notification to user? */
  showToast?: boolean;

  /** Should log the error? */
  logError?: boolean;

  /** Should report to Sentry? */
  reportToSentry?: boolean;

  /** Retry action for recoverable errors */
  retryAction?: () => void | Promise<void>;

  /** Toast duration in ms */
  toastDuration?: number;

  /** Rethrow error after handling? */
  rethrow?: boolean;
}

/**
 * Centralized error handler
 *
 * Handles logging, reporting, and user notification for errors.
 * Converts unknown errors to AppError instances.
 *
 * @example
 * ```typescript
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   handleError(error, {
 *     context: { operation: 'riskyOperation', userId: user.id },
 *     showToast: true,
 *     reportToSentry: true,
 *   });
 * }
 * ```
 */
export function handleError(
  error: unknown,
  options: HandleErrorOptions = {}
): AppError {
  const {
    context,
    showToast: shouldShowToast = true,
    logError: shouldLogError = true,
    reportToSentry: shouldReportToSentry = true,
    retryAction,
    toastDuration,
    rethrow = false,
  } = options;

  // Convert to AppError if necessary
  const appError = normalizeError(error, context);

  // Log error
  if (shouldLogError) {
    logError(appError, context);
  }

  // Report to Sentry
  if (shouldReportToSentry && process.env.NODE_ENV === 'production') {
    reportToSentry(appError, context);
  }

  // Notify user
  if (shouldShowToast && typeof window !== 'undefined') {
    notifyUser(appError, {
      duration: toastDuration,
      action: retryAction,
    });
  }

  // Rethrow if requested
  if (rethrow) {
    throw appError;
  }

  return appError;
}

/**
 * Convert any error to AppError
 */
export function normalizeError(error: unknown, context?: Record<string, any>): AppError {
  // Already an AppError
  if (isAppError(error)) {
    return error;
  }

  // Standard Error object
  if (error instanceof Error) {
    return convertStandardError(error, context);
  }

  // String error
  if (typeof error === 'string') {
    return new AppError({
      code: ErrorCode.UNKNOWN_ERROR,
      message: error,
      metadata: context,
    });
  }

  // Object with error properties
  if (error && typeof error === 'object' && 'message' in error) {
    return new AppError({
      code: ErrorCode.UNKNOWN_ERROR,
      message: String(error.message),
      metadata: { ...context, originalError: error },
    });
  }

  // Completely unknown error type
  return new AppError({
    code: ErrorCode.UNKNOWN_ERROR,
    message: 'An unknown error occurred',
    metadata: { ...context, originalError: error },
  });
}

/**
 * Convert standard Error to AppError based on error properties
 */
function convertStandardError(error: Error, context?: Record<string, any>): AppError {
  const message = error.message.toLowerCase();

  // Network errors
  if (message.includes('network') || message.includes('fetch')) {
    return new AppError({
      code: ErrorCode.NETWORK_ERROR,
      message: error.message,
      cause: error,
      metadata: context,
    });
  }

  // Timeout errors
  if (message.includes('timeout') || message.includes('timed out')) {
    return new AppError({
      code: ErrorCode.TIMEOUT_ERROR,
      message: error.message,
      cause: error,
      metadata: context,
    });
  }

  // Database errors
  if (message.includes('database') || message.includes('sql') || message.includes('query')) {
    return new AppError({
      code: ErrorCode.DATABASE_QUERY_FAILED,
      message: error.message,
      cause: error,
      metadata: context,
    });
  }

  // Auth errors
  if (message.includes('unauthorized') || message.includes('authentication')) {
    return new AppError({
      code: ErrorCode.NOT_AUTHENTICATED,
      message: error.message,
      cause: error,
      metadata: context,
    });
  }

  // Permission errors
  if (message.includes('forbidden') || message.includes('permission')) {
    return new AppError({
      code: ErrorCode.NOT_AUTHORIZED,
      message: error.message,
      cause: error,
      metadata: context,
    });
  }

  // Not found errors
  if (message.includes('not found') || message.includes('404')) {
    return new AppError({
      code: ErrorCode.RESOURCE_NOT_FOUND,
      message: error.message,
      cause: error,
      metadata: context,
    });
  }

  // Validation errors
  if (message.includes('invalid') || message.includes('validation')) {
    return new AppError({
      code: ErrorCode.VALIDATION_FAILED,
      message: error.message,
      cause: error,
      metadata: context,
    });
  }

  // Type errors (programming errors)
  if (error instanceof TypeError) {
    return new AppError({
      code: ErrorCode.TYPE_ERROR,
      message: error.message,
      category: ErrorCategory.PROGRAMMING,
      isOperational: false,
      cause: error,
      metadata: context,
    });
  }

  // Reference errors (programming errors)
  if (error instanceof ReferenceError) {
    return new AppError({
      code: ErrorCode.NULL_REFERENCE,
      message: error.message,
      category: ErrorCategory.PROGRAMMING,
      isOperational: false,
      cause: error,
      metadata: context,
    });
  }

  // Default: unknown error
  return new AppError({
    code: ErrorCode.UNKNOWN_ERROR,
    message: error.message,
    cause: error,
    metadata: context,
  });
}

// ============================================================================
// ASYNC ERROR WRAPPER
// ============================================================================

/**
 * Wrap async function with error handling
 *
 * @example
 * ```typescript
 * const safeFunction = withErrorHandling(
 *   async () => await riskyOperation(),
 *   { context: { operation: 'riskyOperation' } }
 * );
 * ```
 */
export function withErrorHandling<T>(
  fn: () => Promise<T>,
  options: HandleErrorOptions = {}
): Promise<T | null> {
  return fn().catch((error) => {
    handleError(error, options);
    return null;
  });
}

/**
 * Create a safe version of an async function that handles errors
 *
 * @example
 * ```typescript
 * const safeLoadData = createSafeHandler(loadData, {
 *   context: { operation: 'loadData' },
 *   showToast: true,
 * });
 * ```
 */
export function createSafeHandler<TArgs extends any[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  options: HandleErrorOptions = {}
): (...args: TArgs) => Promise<TReturn | null> {
  return async (...args: TArgs) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, options);
      return null;
    }
  };
}

// ============================================================================
// RETRY LOGIC
// ============================================================================

export interface RetryOptions {
  /** Maximum number of retry attempts */
  maxAttempts?: number;

  /** Initial delay between retries in ms */
  initialDelay?: number;

  /** Multiplier for exponential backoff */
  backoffMultiplier?: number;

  /** Maximum delay between retries in ms */
  maxDelay?: number;

  /** Function to determine if error is retryable */
  isRetryable?: (error: AppError) => boolean;

  /** Callback on retry */
  onRetry?: (attempt: number, error: AppError) => void;
}

/**
 * Retry async function with exponential backoff
 *
 * @example
 * ```typescript
 * const data = await withRetry(
 *   () => fetchData(),
 *   { maxAttempts: 3, initialDelay: 1000 }
 * );
 * ```
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    initialDelay = 1000,
    backoffMultiplier = 2,
    maxDelay = 10000,
    isRetryable = (error) => error.recovery.canRetry,
    onRetry,
  } = options;

  let lastError: AppError;
  let delay = initialDelay;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = normalizeError(error);

      // Don't retry if not retryable or last attempt
      if (!isRetryable(lastError) || attempt === maxAttempts) {
        throw lastError;
      }

      // Callback
      onRetry?.(attempt, lastError);

      // Wait before retry
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Exponential backoff
      delay = Math.min(delay * backoffMultiplier, maxDelay);
    }
  }

  throw lastError!;
}
